/**
 * Role Switcher Component
 * КРИТИЧНО: BC-003 - компонент для переключения внутренних ролей
 * 
 * @see A6_BREAKING_CHANGES.json - BC-003 для детального описания
 * @see A2_AUTH_FLOW_GUIDE.json - детали ролевой системы
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  SwapHoriz as SwapHorizIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useAuth } from '../contexts/AuthContext';
import { authApi, type UserRole } from '../services/api/auth.api';
import { useQuery } from '@tanstack/react-query';
import { INTERNAL_ROLE_LABEL_KEYS } from '../types/enums/internal-roles.enum';
import { EVENT_ROLE_LABEL_KEYS } from '../types/enums/event-roles.enum';

/*
 * Подпись роли берётся из общего словаря `roles.*` — того же, что читает
 * навигация. Раньше здесь печаталось само значение перечисления
 * (`CONTENT_MODERATOR`): это идентификатор протокола, а не имя роли. Если роль
 * пришла вне известного набора, остаётся её значение — видно и что пришло, и что
 * подписи на него нет (AD-472).
 */
function getRoleLabel(t: TFunction, r: UserRole | null | undefined): string {
  if (!r) return String(t('roles.switcher.pick'));
  if (r.type === 'event' && r.eventName) return r.eventName;
  const keys: Record<string, string> =
    r.type === 'event' ? EVENT_ROLE_LABEL_KEYS : INTERNAL_ROLE_LABEL_KEYS;
  const key = keys[String(r.role)];
  return key === undefined ? String(r.role) : String(t(key));
}
/**
 * Компонент для переключения внутренних ролей пользователя
 * КРИТИЧНО: BC-003 - использует новую ролевую систему
 * 
 * @example
 * ```tsx
 * <RoleSwitcher />
 * ```
 */
export const RoleSwitcher: React.FC = () => {
  const { t } = useTranslation('common');
  const { user, switchRole, isLoading: authLoading } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isSwitching, setIsSwitching] = useState(false);

  // Загрузка ролей пользователя
  const { data: rolesData, isLoading: rolesLoading } = useQuery(
    ['user-roles'],
    async () => {
      const response = await authApi.getUserRoles();
      return response.data;
    },
    {
      enabled: !!user, // Загружать только если пользователь авторизован
    }
  );

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRoleSwitch = async (roleId: string) => {
    setIsSwitching(true);
    try {
      await switchRole(roleId);
      handleClose();
    } catch (error) {
      console.error('Failed to switch role:', error);
      // TODO: Показать уведомление об ошибке
    } finally {
      setIsSwitching(false);
    }
  };

  // Не показывать, если у пользователя только одна роль или нет ролей
  // КРИТИЧНО: availableRoles из GetUserRolesResponse имеет тип Array<{ id, name, displayName, description?, permissions? }>
  const availableRoles = rolesData?.availableRoles || [];
  if (!rolesData || availableRoles.length <= 1) {
    return null;
  }

  // Используем первую доступную роль как текущую, если currentRole не установлен
  const currentRole = rolesData.currentRole ?? availableRoles[0] ?? null;
  const isLoading = rolesLoading || authLoading || isSwitching;

  return (
    <Box>
      <Tooltip title={t('roles.switcher.tooltip')}>
        <Button
          onClick={handleOpen}
          disabled={isLoading}
          startIcon={<SwapHorizIcon />}
          endIcon={isLoading ? <CircularProgress size={16} /> : undefined}
          variant="outlined"
          sx={{
            minWidth: 200,
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {getRoleLabel(t, currentRole)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('roles.switcher.current')}
            </Typography>
          </Box>
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 320,
            maxWidth: 400,
            mt: 1,
          },
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t('roles.switcher.menuTitle')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('roles.switcher.available', { count: availableRoles.length })}
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
          {availableRoles
            .filter((role: UserRole) => role.type === 'internal')
            .map((role: UserRole) => {
              const isCurrent = role.id === currentRole?.id;

              return (
                <MenuItem
                  key={role.id}
                  onClick={() => !isCurrent && handleRoleSwitch(role.id)}
                  disabled={isCurrent || isLoading}
                  sx={{
                    py: 1.5,
                    px: 2,
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    bgcolor: isCurrent ? 'action.selected' : 'transparent',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 0.5 }}>
                    <PersonIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
                      {getRoleLabel(t, role)}
                    </Typography>
                    {isCurrent && (
                      <CheckCircleIcon sx={{ ml: 1, fontSize: 18, color: 'success.main' }} />
                    )}
                  </Box>

                  {role.type === 'event' && role.eventName && (
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                      {role.eventName}
                    </Typography>
                  )}

                  {role.permissions && role.permissions.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      <Chip
                        label={t('roles.switcher.permissions', {
                          count: role.permissions.length,
                        })}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  )}
                </MenuItem>
              );
            })}
        </Box>

        <Divider />

        <Box sx={{ p: 2, pt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('roles.switcher.note')}
          </Typography>
        </Box>
      </Menu>
    </Box>
  );
};

